/* eslint-disable react-hooks/exhaustive-deps */
import { useForm } from "react-hook-form";
import {
  ControlledInput,
  ControlledSelect,
  FormColorPicker,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
  TagInput,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useNavigate, useParams } from "react-router";
import { FLOAT_REGEX, numbersOnly } from "../../utils/regexValidations";
import {
  buildRewardPayload,
  useMarketPricing,
} from "../../store/useMarketPricing";
import { useEffect, useState } from "react";
import { useUploadImage } from "../../store/useUploadImage";
import Chart from "react-apexcharts";
import { getWeekRange } from "../../services/dateService";
import { withAdminPermission } from "../../utils/HOC/withAdminPermission";
import { useAuth } from "../../store/useAuth";
import { usePartners } from "../../store/usePartners";
import { useDropLocations } from "../../store/useDropLocations";
import {
  ILocationRateRow,
  toLocationRateRows,
  toLocationRates,
  validateLocationRateRows,
} from "../../utils/locationRates";
import { LocationRatesEditor } from "./LocationRatesEditor";

type Inputs = {
  name: string;
  measurement: string;
  rate: number;
  co2_rate: number;
  droppie_revenue_kg?: number;
  droppie_revenue_item?: number;
  droppie_revenue_month?: number;
  kgConversion: number;
  maxAllowed: number;
  cbs: number;
  dropType?: string;
  rewardType?: string;
  voucherRewardPartnerId?: string;
  order?: number;
};

function EditMarketPricingScreen() {
  /** Hooks */
  const params = useParams();
  const {
    getAllMarketPrices,
    weeklyMarketStats,
    getWeeklyMarketStats,
    getMarketPricingStats,
    marketPriceStats,
    updateMarketPricing,
    marketPrices,
    updatedMarketPricing,
    clearUpdatedMarketPricing,
    getMarketPricingHistory,
    marketPricingHistory,
    isLoadingMarketPricingHistory,
    clearMarketPricingHistory,
  } = useMarketPricing();
  const { user } = useAuth();
  const { uploadImage, clearUploadedImage, uploadedImage, setUploadedImage } =
    useUploadImage();
  const { allPartners, getPartnersForSelect } = usePartners();
  const { dropLocations, getDropLocations } = useDropLocations();
  const navigate = useNavigate();
  const {
    reset,
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [keywords, setKeywords] = useState<string[]>([]);
  const [primaryColor, setPrimaryColor] = useState<string | null>(null);
  const [secondaryColor, setSecondaryColor] = useState<string | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [weeklyRange, setWeeklyRange] = useState(new Date());
  const [locationRateRows, setLocationRateRows] = useState<ILocationRateRow[]>(
    []
  );
  const [showLocationRateErrors, setShowLocationRateErrors] = useState(false);
  const isSuperAdmin = Number(user?.user_role) === 7;
  const locationRateErrors = validateLocationRateRows(locationRateRows);
  // Location exceptions the material has now; a voucher stream loses them on save.
  const savedLocationRateCount =
    marketPrices?.find((mp) => mp._id === params?.id)?.locationRates?.length ?? 0;

  const { startOfWeek, endOfWeek } = getWeekRange(weeklyRange);

  /** Functions */
  const getLastWeek = () =>
    setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() - 7)));
  const getNextWeek = () =>
    setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() + 7)));

  const goBack = () => navigate(-1);
  const onSubmit = (data: Inputs) => {
    if (!uploadedImage) {
      return setSubmitted(true);
    }
    const sendLocationRates =
      isSuperAdmin && data?.rewardType !== "PARTNER_VOUCHER";
    if (sendLocationRates && locationRateErrors.some(Boolean)) {
      return setShowLocationRateErrors(true);
    }

    // voucherRewardPartnerId is a form-only field — buildRewardPayload turns
    // it into the nested voucherReward object below, so it must not be spread
    // into the request as-is. Typed as Record so the order key can be deleted
    // when the field is left blank.
    const { voucherRewardPartnerId: _vp, ...formData } = data;
    const payload: Record<string, unknown> = {
      ...formData,
      type: data?.name?.toLowerCase(),
      image: uploadedImage,
      keywords,
      primaryColor,
      secondaryColor,
      // Voucher streams have no consumer payout: rate 0, which the backend
      // also enforces on every save of a voucher stream.
      rate: data?.rewardType === "PARTNER_VOUCHER" ? 0 : data?.rate,
      ...buildRewardPayload(data),
    };
    // Number() so "0" is sent as 0 rather than the string a text input
    // produces — the backend now accepts 0 as a real position (top of list).
    if (data.order === undefined || String(data.order).trim() === "") {
      delete payload.order;
    } else {
      payload.order = Number(data.order);
    }
    // Only super admins may change location rates (the backend refuses them
    // from anyone else). A voucher stream has none: the backend removes them
    // when it is saved as one, so they are not sent.
    if (sendLocationRates) {
      payload.locationRates = toLocationRates(locationRateRows);
    }
    if (!isSuperAdmin) {
      // The backend refuses these from anyone but a super admin, and the
      // reward fields are always in the payload, so they are left out here.
      const { maxAllowed: _, rewardType: _rt, voucherReward: _vr, ...safePayload } = payload;
      updateMarketPricing(params?.id as string, safePayload);
      return;
    }
    updateMarketPricing(params?.id as string, payload);
  };

  /** Effects */
  useEffect(() => {
    if (params && marketPrices?.length) {
      const oneMarketPrice = marketPrices?.find((mp) => mp._id === params.id);
      reset({
        name: oneMarketPrice?.name ?? "",
        measurement: oneMarketPrice?.measurement ?? "",
        rate: Number(oneMarketPrice?.rate ?? 0),
        co2_rate: Number(oneMarketPrice?.co2_rate ?? 0),
        droppie_revenue_kg: Number(oneMarketPrice?.droppie_revenue_kg ?? 0),
        droppie_revenue_item: Number(oneMarketPrice?.droppie_revenue_item ?? 0),
        droppie_revenue_month: Number(
          oneMarketPrice?.droppie_revenue_month ?? 0
        ),
        kgConversion: Number(oneMarketPrice?.kgConversion ?? 0),
        maxAllowed: Number(oneMarketPrice?.maxAllowed ?? 0),
        cbs: Number(oneMarketPrice?.cbs ?? 0),
        dropType: oneMarketPrice?.dropType ?? "",
        rewardType: oneMarketPrice?.rewardType ?? "CASH",
        // partnerId is a raw ObjectId string on unpopulated docs; handle a
        // populated object defensively.
        voucherRewardPartnerId:
          typeof oneMarketPrice?.voucherReward?.partnerId === "object"
            ? ((oneMarketPrice?.voucherReward?.partnerId as any)?._id ?? "")
            : (oneMarketPrice?.voucherReward?.partnerId ?? ""),
        order: Number(oneMarketPrice?.order ?? 0),
      });
      setUploadedImage(oneMarketPrice?.image as string);
      setKeywords(
        oneMarketPrice?.keywords?.length ? oneMarketPrice?.keywords : []
      );
      setPrimaryColor(oneMarketPrice?.primaryColor ?? null);
      setSecondaryColor(oneMarketPrice?.secondaryColor ?? null);
      setLocationRateRows(toLocationRateRows(oneMarketPrice?.locationRates));
    }
  }, [params, marketPrices]);

  useEffect(() => {
    getWeeklyMarketStats(params?.id as string, weeklyRange);
  }, [weeklyRange]);

  useEffect(() => {
    getMarketPricingStats(params.id as string);
    getWeeklyMarketStats(params.id as string);
    getAllMarketPrices();
    getMarketPricingHistory(params.id as string);
    getPartnersForSelect();
    getDropLocations();
    return () => {
      clearUploadedImage();
      clearMarketPricingHistory();
    };
  }, []);

  useEffect(() => {
    if (updatedMarketPricing) {
      clearUpdatedMarketPricing();
      goBack();
    }
  }, [updatedMarketPricing]);

  // Voucher streams have no consumer payout: the rate input is hidden and the
  // value pinned to 0 so the (possibly retained) required/pattern rules pass.
  // Converting a cash stream to a voucher stream zeroes its rate — the price
  // history log records that change.
  const rewardTypeValue = watch("rewardType");
  useEffect(() => {
    if (rewardTypeValue === "PARTNER_VOUCHER") {
      setValue("rate", 0, { shouldValidate: true });
    }
  }, [rewardTypeValue, setValue]);

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const imageError = submitted && !image;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex w-1/2 justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-11">
          Edit Stream options
        </h1>
      </div>
      {/* Top Stats */}
      <div className="flex flex-col sm:flex-row w-full p-2 sm:p-7 bg-white  border-gray-border drop-shadow-none rounded-xl sm:h-28 mb-1 sm:mb-10">
        <div className="flex flex-1 sm:flex-col justify-between items-center   sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              {marketPriceStats?.totalDrops ?? 0}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Total drops per material
          </p>
        </div>
        <div className="flex flex-1 sm:flex-col justify-between items-center   sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              {marketPriceStats?.peopleCount ?? 0}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Total accounts per material
          </p>
        </div>
        <div className="flex flex-1 sm:flex-col justify-between items-center sm:items-center border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              {new Intl.NumberFormat("nl-NL", {
                style: "currency",
                currency: "EUR",
              }).format(
                Number(marketPriceStats?.totalItems ?? 0) *
                  Number(watch("rate") ?? 0)
              )}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Total value per material
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row w-full">
        <div className="w-full sm:w-1/2 bg-white  border-gray-border drop-shadow- rounded-xl">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Edit Stream option
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <ControlledInput
              name="name"
              control={control}
              label="Name"
              placeholder="Add name"
              type="text"
              rules={{ required: true }}
              error={Boolean(errors?.name)}
            />
            <ControlledInput
              name="order"
              control={control}
              label="List order (lower shows first in the app)"
              placeholder="e.g. 10"
              type="text"
              rules={{ required: false, pattern: numbersOnly }}
              error={Boolean(
                // @ts-ignore
                errors?.order || !numbersOnly.test(String(watch("order") ?? ""))
              )}
            />
            <ControlledSelect
              name="measurement"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.measurement)}
              label="Measurement type"
              options={[
                { _id: "kg", title: "Kg" },
                { _id: "unit", title: "Item" },
                { _id: "statiegeld", title: "Statiegeld" },
              ]}
              placeholder="Pick measurement type here"
            />
            <ControlledSelect
              name="dropType"
              control={control}
              rules={{ required: false }}
              error={Boolean(errors?.dropType)}
              label="Drop type"
              options={[
                { _id: "default", title: "Default" },
                { _id: "statiegeld", title: "Statiegeld" },
              ]}
              placeholder="Pick drop type here"
            />
            {/* Switching the reward type zeroes the consumer payout rate as a
                side effect, so it carries (at least) the same weight as
                editing the rate — gate both to super admins, like `rate`. */}
            <ControlledSelect
              name="rewardType"
              control={control}
              rules={{ required: false }}
              error={Boolean(errors?.rewardType)}
              label="Reward type"
              options={[
                { _id: "CASH", title: "Cash (balance credit)" },
                { _id: "PARTNER_VOUCHER", title: "Partner voucher" },
              ]}
              placeholder="Cash (balance credit)"
              disabled={!isSuperAdmin}
            />
            {watch("rewardType") === "PARTNER_VOUCHER" ? (
              <ControlledSelect
                name="voucherRewardPartnerId"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.voucherRewardPartnerId)}
                label="Voucher partner"
                options={allPartners.map((partner) => ({
                  _id: partner._id as string,
                  title: partner.name as string,
                }))}
                placeholder="Pick the partner whose vouchers this stream issues"
                disabled={!isSuperAdmin}
              />
            ) : null}
            {watch("rewardType") !== "PARTNER_VOUCHER" ? (
              <ControlledInput
                name="rate"
                control={control}
                label="Consumer payout rate"
                placeholder="Add rate"
                type="text"
                rules={{ required: true, pattern: FLOAT_REGEX }}
                error={Boolean(
                  // @ts-ignore
                  errors?.rate || !FLOAT_REGEX.test(watch("rate") ?? 0)
                )}
                disabled={!isSuperAdmin}
              />
            ) : null}
            <ControlledInput
              name="droppie_revenue_kg"
              control={control}
              label="Droppie revenue (kg)"
              placeholder="Add Droppie revenue per kg"
              type="text"
              rules={{ required: false, pattern: FLOAT_REGEX }}
              error={Boolean(
                errors?.droppie_revenue_kg ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("droppie_revenue_kg") ?? 0)
              )}
            />
            {watch("rewardType") === "PARTNER_VOUCHER" &&
            savedLocationRateCount > 0 ? (
              <p className="font-satoshi text-sm text-droppie-oranje mb-4">
                Saving this material as a partner voucher stream removes its{" "}
                {savedLocationRateCount} location exception
                {savedLocationRateCount === 1 ? "" : "s"}: voucher streams pay
                no cash, so they have none.
              </p>
            ) : null}
            {watch("rewardType") !== "PARTNER_VOUCHER" ? (
              <LocationRatesEditor
                defaultRevenue={watch("droppie_revenue_kg")}
                rows={locationRateRows}
                onChange={setLocationRateRows}
                locations={dropLocations ?? []}
                errors={locationRateErrors}
                showErrors={showLocationRateErrors}
                disabled={!isSuperAdmin}
              />
            ) : null}
            <ControlledInput
              name="droppie_revenue_item"
              control={control}
              label="Droppie revenue (item)"
              placeholder="Add Droppie revenue per item"
              type="text"
              rules={{ required: false, pattern: FLOAT_REGEX }}
              error={Boolean(
                errors?.droppie_revenue_item ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("droppie_revenue_item") ?? 0)
              )}
            />
            <ControlledInput
              name="droppie_revenue_month"
              control={control}
              label="Droppie revenue (month)"
              placeholder="Add Droppie revenue per month"
              type="text"
              rules={{ required: false, pattern: FLOAT_REGEX }}
              error={Boolean(
                errors?.droppie_revenue_month ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("droppie_revenue_month") ?? 0)
              )}
            />
            <ControlledInput
              name="maxAllowed"
              control={control}
              label="Max (kg/items) Allowed"
              placeholder="Add Max Allowed"
              type="text"
              rules={
                isSuperAdmin
                  ? { required: true, pattern: FLOAT_REGEX }
                  : { required: false, pattern: FLOAT_REGEX }
              }
              disabled={!isSuperAdmin}
              error={Boolean(
                errors?.maxAllowed ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("maxAllowed") ?? 0)
              )}
            />
            <ControlledInput
              name="co2_rate"
              control={control}
              label="CO2 Rate"
              placeholder="Add CO2 Rate"
              type="text"
              rules={{ required: true, pattern: FLOAT_REGEX }}
              error={Boolean(
                // @ts-ignore
                errors?.co2_rate || !FLOAT_REGEX.test(watch("co2_rate") ?? 0)
              )}
            />
            {watch("measurement") === "unit" ? (
              <ControlledInput
                name="kgConversion"
                control={control}
                label="KG Conversion"
                placeholder="Add kg conversion per item"
                type="text"
                rules={{ required: true, pattern: FLOAT_REGEX }}
                error={Boolean(
                  errors?.kgConversion ||
                  // @ts-ignore
                  !FLOAT_REGEX.test(watch("kgConversion") ?? 0)
                )}
              />
            ) : null}
            <ControlledInput
              name="cbs"
              control={control}
              label="CBS Data"
              placeholder="Add CBS data"
              type="text"
              rules={{ required: true, pattern: FLOAT_REGEX }}
              error={Boolean(
                // @ts-ignore
                errors?.cbs || !FLOAT_REGEX.test(watch("co2_rate") ?? 0)
              )}
            />
            <TagInput
              label="Keywords"
              keywords={keywords}
              setKeywords={setKeywords}
            />
            <FormColorPicker
              label="Primary color (container bg)"
              value={primaryColor ?? "#D7E3F7"}
              onChange={setPrimaryColor}
            />
            <FormColorPicker
              label="Secondary color (DOs/DON'Ts, accent)"
              value={secondaryColor ?? "#FF6E41"}
              onChange={setSecondaryColor}
            />
            <FormUpload
              label="Field icon"
              onChange={onImageChange}
              value={image}
              error={imageError}
              imageUrl={uploadedImage as string}
            />
          </div>
        </div>
        {/* Pay-out */}
        <div className="flex flex-col w-full sm:w-1/2 p-2 sm:p-7 bg-white rounded-xl border-gray-border  mb-1 sm:mb-10 ml-0 sm:ml-3">
          <div className="flex flex-col sm:flex-row w-full items-start justify-between">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-2 sm:mb-12">
              {/* Volumes of the week */}
              Pay-out per stream (cumulative) per week
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
              <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
                <PrimaryButton
                  type="button"
                  onClick={getLastWeek}
                  label="Prev"
                />
                <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                  {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
                </div>
                <PrimaryButton
                  type="button"
                  disabled={endOfWeek > new Date()}
                  onClick={getNextWeek}
                  label="Next"
                />
              </div>
            </div>
          </div>
          {/* @ts-ignore */}
          {Object.values(weeklyMarketStats ?? {})?.length ? (
            // @ts-ignore
            <Chart
              {...chartConfig}
              // @ts-ignore
              options={{
                ...chartConfig?.options,
                xaxis: {
                  ...chartConfig?.options?.xaxis,
                  categories: Object.keys(weeklyMarketStats ?? {}),
                },
                // options?.xaxis?.categories = []
              }}
              series={[
                {
                  name: "€",
                  data: Object.values(weeklyMarketStats ?? {})?.map((wms) =>
                    Number(Number(wms?.totalPrice ?? 0) / 100)?.toFixed(2)
                  ),
                },
              ]}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                No data found
              </p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row w-full items-start justify-between">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-2 sm:mb-12">
              {/* Volumes of the week */}
              Tonnage per stream (cumulative) per week
            </p>
          </div>
          {/* @ts-ignore */}
          {Object.values(weeklyMarketStats ?? {})?.length ? (
            // @ts-ignore
            <Chart
              {...chartConfig}
              // @ts-ignore
              options={{
                ...chartConfig?.options,
                xaxis: {
                  ...chartConfig?.options?.xaxis,
                  categories: Object.keys(weeklyMarketStats ?? {}),
                },
                // options?.xaxis?.categories = []
              }}
              series={[
                {
                  name: `${watch("measurement")}`,
                  data: Object.values(weeklyMarketStats ?? {})?.map((wms) =>
                    Number(Number(wms?.totalWeight ?? 0))?.toFixed(2)
                  ),
                },
              ]}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                No data found
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Audit log — chronological list of every price change. Populated by
          GET /drops/market/:id/history; empty for materials with no edits yet. */}
      <div className="w-full bg-white rounded-xl border-gray-border mb-1 sm:mb-10 mt-4">
        <div className="w-full border-b border-gray-border px-7 py-[15px] flex items-center justify-between">
          <p className="font-satoshi font-medium text-base text-dark">
            Price change history
          </p>
          {marketPricingHistory?.total ? (
            <p className="font-satoshi text-sm text-gray-text">
              {marketPricingHistory.total} change
              {marketPricingHistory.total === 1 ? "" : "s"}
            </p>
          ) : null}
        </div>
        <div className="px-7 py-5 overflow-x-auto">
          {isLoadingMarketPricingHistory ? (
            <p className="font-satoshi text-sm text-gray-text">Loading…</p>
          ) : !marketPricingHistory?.items?.length ? (
            <p className="font-satoshi text-sm text-gray-text">
              No price changes recorded yet.
            </p>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-border">
                  <th className="py-2 pr-4 font-satoshi text-sm font-semibold text-dark whitespace-nowrap">
                    Date
                  </th>
                  <th className="py-2 pr-4 font-satoshi text-sm font-semibold text-dark whitespace-nowrap">
                    Changed by
                  </th>
                  <th className="py-2 pr-4 font-satoshi text-sm font-semibold text-dark">
                    Changes
                  </th>
                </tr>
              </thead>
              <tbody>
                {marketPricingHistory.items.map((entry) => (
                  <tr
                    key={entry._id}
                    className="border-b border-gray-border last:border-b-0 align-top"
                  >
                    <td className="py-3 pr-4 font-satoshi text-sm text-dark whitespace-nowrap">
                      {formatHistoryDate(entry.createdAt)}
                    </td>
                    <td className="py-3 pr-4 font-satoshi text-sm text-dark whitespace-nowrap">
                      {formatChangedBy(entry)}
                    </td>
                    <td className="py-3 pr-4 font-satoshi text-sm text-dark">
                      {entry.reason ? (
                        <p className="text-gray-text italic mb-1">
                          {entry.reason}
                        </p>
                      ) : null}
                      {entry.changedFields.length === 0 ? (
                        <span className="text-gray-text">—</span>
                      ) : (
                        <ul className="space-y-1">
                          {entry.changedFields.map((field) => (
                            <li key={field}>
                              <span className="font-medium">
                                {humanizeFieldName(field)}
                              </span>
                              :{" "}
                              <span className="text-gray-text">
                                {formatFieldValue(entry.before?.[field])}
                              </span>
                              <span className="mx-1">→</span>
                              <span className="text-dark">
                                {formatFieldValue(entry.after?.[field])}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="w-1/2 flex flex-row items-center justify-end mt-[29px]">
        <SecondaryButton type="button" onClick={goBack} label="Cancel" />
        <PrimaryButton type="submit" className="ml-4" label="Save" />
      </div>
    </form>
  );
}

// Pretty labels mirror the form's own input labels so the audit row reads
// the same as the admin sees them in the edit form above.
const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  type: "Type slug",
  rate: "Consumer payout rate",
  co2_rate: "CO2 rate",
  droppie_revenue_kg: "Droppie revenue (kg)",
  droppie_revenue_item: "Droppie revenue (item)",
  droppie_revenue_month: "Droppie revenue (month)",
  kgConversion: "KG conversion",
  maxAllowed: "Max allowed",
  cbs: "CBS",
  measurement: "Measurement",
  dropType: "Drop type",
  isActive: "Active",
  order: "Order",
  linkedArticle: "Linked article",
  rewardType: "Reward type",
  locationRates: "Location exceptions (rate, revenue)",
};
const humanizeFieldName = (field: string) => FIELD_LABELS[field] ?? field;

const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  return String(value);
};

const formatHistoryDate = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatChangedBy = (
  entry: NonNullable<
    ReturnType<typeof useMarketPricing.getState>["marketPricingHistory"]
  >["items"][number],
): string => {
  if (entry.changedBy && typeof entry.changedBy === "object") {
    const fn = (entry.changedBy as any).first_name ?? "";
    const ln = (entry.changedBy as any).last_name ?? "";
    const email = (entry.changedBy as any).email ?? "";
    const name = `${fn} ${ln}`.trim();
    return name || email || "Unknown";
  }
  if (entry.changedByName) return entry.changedByName;
  return "System";
};

export default withProtectedRoute(withAdminPermission(EditMarketPricingScreen));

const chartConfig = {
  type: "bar",
  width: "100%",
  height: 240,
  options: {
    chart: {
      toolbar: {
        show: false,
      },
    },
    title: {
      show: "",
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#194128"],
    plotOptions: {
      bar: {
        columnWidth: 12,
        borderRadius: 2,
      },
    },
    xaxis: {
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "14px",
          fontFamily: "Satoshi",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "14px",
          fontFamily: "Satoshi",
          fontWeight: 500,
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#dddddd",
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 5,
        right: 0,
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      theme: "dark",
    },
  },
};
